import { Injectable, NgZone } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import moment from 'moment';

import { Subscription } from 'rxjs';

import { HlsUtilService, setDisabledManifestRewriteHeaderFlag } from '@vbrick/vbrick-player';

import { DateParsersService } from 'rev-shared/date/DateParsers.Service';
import { HlsFileUtilService } from 'rev-shared/videoPlayer/hlsFileUtil/HLsFileUtil.Service';
import { IUnsubscribe } from 'rev-shared/push/IUnsubscribe';
import { MinuteMs, SecondMs } from 'rev-shared/date/Time.Constant';
import { PushBus } from 'rev-shared/push/PushBus.Service';
import { REFLECTION_STREAM_NAME_SUFFIX } from 'rev-shared/util/StreamNameUtil';
import { UserLocalIPService } from 'rev-shared/security/UserLocalIP.Service';
import { VideoService } from 'rev-shared/media/Video.Service';
import { isNumber, noop } from 'rev-shared/util';
import { lastValueFrom } from 'rev-shared/rxjs/lastValueFrom';
import { readProducerOptions } from 'rev-shared/webcast/webcastView/producer/Producer.Service';
import { getPlaybackTokenConfig, removeWithCredParam } from 'rev-shared/videoPlayer/PlaybackTokenUtility';
import { ResourceType } from 'rev-shared/videoPlayer/ResourceType';

import { hasCdnPushedPlayback, isAkamaiPushedPlaybackReady, isVbrickCdnPushedPlaybackReady } from '../CdnUtil';
import { isApple } from 'rev-shared/util/UserAgentUtil';
import { updateWaitingStatusForWebexMeeting, updateWaitingStatusForVodSource } from '../WebcastStatusUpdateHelper';
import { VC_STATUS } from './VcStatus';
import { WebcastConnectionService } from './WebcastConnection.Service';
import { WebcastModel } from './WebcastModel';
import { WebcastRunStatus, WebcastRunType, RunItem } from './RunItem';
import { WebcastStatus } from '../WebcastStatus';
import { WebcastVideoSource } from '../WebcastVideoSource';
import { StreamType } from 'rev-shared/videoPlayer/StreamType';
import { getReactionSettings } from '../Webcast.Service';

const waitForHlsLiveStreamTimeoutMs = MinuteMs + 30000; //giving little extra time as MPH will be retrying for 1 min.
const waitForHlsLiveStreamCheckIntervalMs = 2 * SecondMs;
const VIDEO_FORMAT_HLS: string = 'HLS';

@Injectable({
	providedIn: 'root'
})
export class WebcastModelService {
	constructor(
		private readonly DateParsers: DateParsersService,
		private readonly HlsFileUtil: HlsFileUtilService,
		private readonly HlsUtilService: HlsUtilService,
		private readonly PushBus: PushBus,
		private readonly UserLocalIPService: UserLocalIPService,
		private readonly VideoService: VideoService,
		private readonly WebcastConnectionService: WebcastConnectionService,
		private readonly http: HttpClient,
		private readonly zone: NgZone
	) {}

	private updateWebcastPlaybacks(webcast: WebcastModel, checkPlaybacksLocally?: boolean): Promise<void> {
		if(webcast.currentUser.isSinglePresenter) {
			//Presenter doesn't play back the same video stream.
			return Promise.resolve();
		}

		webcast.isUpdatingPlaybacks = true;
		let playbacks: any[] = [];

		if(checkPlaybacksLocally) {
			playbacks = webcast.isSecondaryPlaying ? webcast.secondaryPlaybacks : webcast.primaryPlaybacks;
		}

		const playbacksPromise: Promise<any[]> = playbacks?.length
			? Promise.resolve(playbacks)
			: this.getWebcastVideoUrl(webcast);

		return playbacksPromise
			.then(playbacks => {
				const firstPlayback = playbacks?.[0];
				const isFirstPlaybackCustomDevice = firstPlayback?.name?.endsWith(REFLECTION_STREAM_NAME_SUFFIX);
				const isFirstPlaybackHls: boolean = firstPlayback?.videoFormat === VIDEO_FORMAT_HLS;
				const isFirstPlaybackMulticastHls: boolean = firstPlayback && this.HlsUtilService.isMulticast(firstPlayback.url, null);
				const isFirstPlaybackVciHls = isFirstPlaybackHls && webcast.dualPlaybackSource;
				const isFirstPlaybackRevConnect = !!firstPlayback?.revConnectInfo;
				const isFirstPlaybackVodWebcastHls = isFirstPlaybackHls && webcast.isVodSource;
				const isFirstPlaybackRtmpHls = isFirstPlaybackHls && webcast.isRtmpStreamType;
				const isFirstPlaybackWebrtcSinglePresenterHls = isFirstPlaybackHls && webcast.isWebrtcSinglePresenter;

				const isFirstPlaybackHlsWithSubtitles = isFirstPlaybackHls
					&& webcast.liveSubtitles?.isLiveSubtitlesEnabled;

				const skipChildPlaylistCheck = isFirstPlaybackVciHls || isFirstPlaybackHlsWithSubtitles;

				const isTargetedSourceForTesting: boolean = !isFirstPlaybackMulticastHls && (
					isFirstPlaybackCustomDevice ||
					isFirstPlaybackVciHls ||
					isFirstPlaybackVodWebcastHls ||
					isFirstPlaybackRtmpHls ||
					isFirstPlaybackWebrtcSinglePresenterHls ||
					isFirstPlaybackHlsWithSubtitles ||
					hasCdnPushedPlayback(playbacks) ||
					isFirstPlaybackRevConnect //added this because rev connect can have push-to-cdn hls. no way to figure out if normal hls or push-to-cdn.
				);
				const isStartupForEventAdmin: boolean = webcast.currentUser.isEventAdmin && !webcast.webcastStatus.isScheduled;
				const isUserEligibleForPlayback: boolean = isStartupForEventAdmin || webcast.webcastStatus.isBroadcasting;

				if (isUserEligibleForPlayback && isTargetedSourceForTesting) {
					const timeoutPromise = new Promise<void>(resolve =>
						window.setTimeout(() => {
							webcast.waitingForVideoSource = true;

							if (isFirstPlaybackVciHls) {
								webcast.vcStatus.update(VC_STATUS.WaitingForStream, true, true);
							}

							resolve();
						})
					);

					return timeoutPromise
						.then(() => this.HlsFileUtil.waitForLivePlaybacks(playbacks, waitForHlsLiveStreamTimeoutMs, waitForHlsLiveStreamCheckIntervalMs, skipChildPlaylistCheck));
				}

				return playbacks;
			})
			.then(playbacks => {
				webcast.playbacks = playbacks;
				webcast.waitingForVideoSource = false;
				webcast.startingVideoSource = false;
				webcast.isUpdatingPlaybacks = false;
				webcast.vcStatus.update(VC_STATUS.Recording, true, true);
			});
	}

	private getWebcastVideoUrl(webcast: WebcastModel): Promise<any[]> {
		return this.UserLocalIPService.checkUserLocation()
			.then(() => lastValueFrom(this.http.get<any>(`/scheduled-events/${webcast.id}/video`)))
			.then(result => {
				const playbacks = this.shapePlaybacks(webcast.id, result.playbackResults || []);
				webcast.primaryPlaybacks = playbacks.filter(p => !p.secondary);
				webcast.secondaryPlaybacks = playbacks.filter(p => p.secondary);

				const activePlaybacks = webcast.isSecondaryPlaying ? webcast.secondaryPlaybacks : webcast.primaryPlaybacks;

				if(!activePlaybacks?.length) {
					return [];
				}

				const multicastPlayback = activePlaybacks.find(p => p.streamType === StreamType.Multicast);
				if (!multicastPlayback) {
					return activePlaybacks;
				}

				//VBM agent added a logic to modify manifest but due to time constraints, we are not able to test for rev player.
				//So, we are disabling it for now.
				return this.HlsUtilService.isDisabledManifestRewriteHeader(multicastPlayback.url)
					.then(add => {
						if (add) {
							setDisabledManifestRewriteHeaderFlag({ apply: true, value: 'true' });
						}
						return activePlaybacks;
					});
			});
	}

	private shapePlaybacks(webcastId: string, playbacks: any[]): any[] {
		return playbacks
			.map(p => {
				const tokenConfig = getPlaybackTokenConfig(webcastId, ResourceType.Webcast, p.url);
				if (tokenConfig) {
					p.tokenConfig = { ...tokenConfig, tokenFetchTimeMs: Date.now() };
				}
				p.url = removeWithCredParam(p.url);
				return p;
			});
	}

	public updateLinkedVideo(webcast: WebcastModel): Promise<void> {
		if(!webcast.linkedVideoId){
			webcast.linkedVideo = null;
		}
		else if(!webcast.linkedVideo || webcast.linkedVideo.id !== webcast.linkedVideoId) {
			webcast.linkedVideo = null;
			return this.VideoService.getVideoBasicInfo(webcast.linkedVideoId)
				.then(v => {
					webcast.linkedVideo = v;
				});
		}

		return Promise.resolve();
	}

	private waitForVideoSource(webcast: WebcastModel): Promise<void> {
		return this.HlsFileUtil.waitForLivePlaybacks(webcast.playbacks, waitForHlsLiveStreamTimeoutMs, waitForHlsLiveStreamCheckIntervalMs)
			.then(playbacks => {
				window.setTimeout(() => {
					webcast.playbacks = playbacks;
					webcast.waitingForVideoSource = false;
					webcast.startingVideoSource = false;
				});
			});
	}

	public registerWebcastPushHandlers(webcast: WebcastModel): IUnsubscribe {
		const accountDevicesSubscription = this.PushBus.subscribe(webcast.accountId, 'ScheduledEventsDevices', {
			DeviceUpdated: ({ deviceId, isActive }) => { //TODO: move this message to deviceId route
				if(deviceId === webcast.presentationProfileDeviceId){
					webcast.presentationProfileDeviceActive = isActive;
				}
			}
		});

		const webcastAdminSubscription = webcast.currentUser.isEventAdmin &&
			this.PushBus.subscribe(webcast.id, 'Webcast.EventAdmin', {
				PresenterStatusSet: ({ isPresenterReady }) => {
					if(!webcast.currentUser.isSinglePresenter) {
						webcast.update({ isPresenterReady });
					}
				}
			});

		const webcastSubscription = this.PushBus.subscribe(webcast.id, {
			WebcastDetailsSaved: ({ preProduction, nextPreProductionRunNumber, ...msgData }) => {

				if(preProduction &&
					isNumber(nextPreProductionRunNumber) && (
					!webcast.preProductionRun ||
					webcast.preProductionRun.runNumber !== nextPreProductionRunNumber
				)) {
					webcast.addRunItem({
						runNumber: nextPreProductionRunNumber,
						runType: WebcastRunType.PreProduction,
						status: WebcastRunStatus.Scheduled
					});
				}
				else if( !preProduction &&
					webcast.preProduction) {
					webcast.removePreProdRuns();
				}

				webcast.update({
					accessControl: msgData.accessControl,
					attendeeJoinMethod: msgData.attendeeJoinMethod,
					automatedWebcast: msgData.automatedWebcast,
					autoplay: msgData.autoplay,
					autoAssociateVod: msgData.autoAssociateVod,
					backgroundImages: msgData.backgroundImages,
					chatEnabled: msgData.chatEnabled,
					closedCaptionsEnabled: msgData.closedCaptionsEnabled,
					description: msgData.description,
					disableAutoRecording: msgData.disableAutoRecording,
					endDate: this.DateParsers.parseUTCDate(msgData.endDate),
					embeddedContent: msgData.embeddedContent,
					eventAdminIds: msgData.eventAdminIds,
					isBackgroundFill: msgData.isBackgroundFill,
					liveSubtitles: {
						isLiveSubtitlesEnabled: !!msgData.liveSubtitles,
						...msgData.liveSubtitles
					},
					lobbyTimeMinutes: msgData.lobbyTimeMinutes,
					moderatorIds: msgData.moderatorIds,
					password: msgData.password,
					pollsEnabled: msgData.pollsEnabled,
					userPollsResponse: msgData.userPollsResponse,
					preProduction: preProduction && {
						...preProduction,
						durationMs: this.DateParsers.parseTimespan(preProduction.duration)
					},
					presentationFileDownloadAllowed: msgData.presentationFileDownloadAllowed,
					presentationProfileDeviceId: msgData.presentationProfileDeviceId,
					presentationProfileId: msgData.presentationProfileId,
					presenterId: msgData.presenterId,
					presenterIds: msgData.presenterIds,
					bannerDetails: msgData.bannerDetails,
					questionAndAnswerEnabled: msgData.questionAndAnswerEnabled,
					questionOption: msgData.questionOption,
					reactionsSettings: getReactionSettings(msgData.reactionsSettings),
					redirectVod: msgData.redirectVod,
					rtmpSettings: msgData.rtmpSettings ? {
						...msgData.rtmpSettings,
						backupStreamEnabled: !!msgData.secondaryRtmpSettings?.pushUrl,
						backupStreamUrl: msgData.secondaryRtmpSettings?.pushUrl,
						backupStreamKey: msgData.secondaryRtmpSettings?.streamKey
					} : null,
					shortcutName: msgData.shortcutName,
					startDate: this.DateParsers.parseUTCDate(msgData.startDate),
					title: msgData.title,
					unlisted: msgData.unlisted,
					userIdSlideControl: msgData.userIdSlideControl,
					vcSipAddress: msgData.vcSipAddress,
					vcMicrosoftTeamsMeetingUrl: msgData.vcMicrosoftTeamsMeetingUrl,
					vcDtmf: msgData.vcDtmf,
					vcDtmfControlsEnabled: msgData.dtmfControlsEnabled,
					webexTeam: msgData.webexTeam,
					categoryIds: msgData.categoryIds,
					customFields: msgData.customFields,
					enableCustomBranding: msgData.enableCustomBranding,
					brandingSettings: msgData.brandingSettings,
					emailToPreRegistrants: msgData.emailToPreRegistrants,
					videoSource: msgData.videoSource,
					vodInfo: {
						...msgData.vodInfo,
						durationMs: this.DateParsers.parseTimespan(msgData.vodInfo?.duration)
					},
					viewerIdEnabled: msgData.viewerIdEnabled,
					producerOptions: msgData.videoSource === WebcastVideoSource.PRODUCER
						? webcast.producerOptions || readProducerOptions()
						: null,
					viewerIdWatermarkText: msgData.viewerIdWatermarkText,
					additionalEmbedDomains: msgData.additionalEmbedDomains

				});
			},

			WebcastStarting: ({ runNumber, startingVideoSource }) => {
				webcast.vcStatus.reset();
				webcast.webcastStatus.reset();

				webcast.startingVideoSource = startingVideoSource;
				webcast.webcastStatus.isStarting = true;

				if (webcast.isVodSource) {
					updateWaitingStatusForVodSource(webcast);
				}

				this.updateRunItem(webcast, runNumber, run => run.status = WebcastRunStatus.Starting);
			},

			WebcastStarted: ({ runItem, webcastStatus }) => {
				if (webcastStatus === WebcastStatus.VideoSourceStarting) {
					this.updateWebexMeetingEvent(webcast, runItem);
					return;
				}

				if (webcast.isAutomated ||
					webcast.currentUser.isEventAdmin ||
					(webcast.currentUser.isEventAdminOrModerator && webcast.isVodSource)
				) {
					this.updateWebcastPlaybacks(webcast)
						.catch(e => console.error('updateWebcastPlaybacks', e))
						.finally(() => webcast.startingVideoSource = false);
				}

				webcast.webcastStatus.isInProgress = true;
				this.updateRunItem(
					webcast,
					runItem.runNumber,
					run => {
						run.status = WebcastRunStatus.InProgress;
						run.startDate = this.DateParsers.parseUTCDate(runItem.startDate);
					}
				);
				webcast.update(webcast);
			},
			WebexLiveStreamToWebcastStarting: ({ run }) => {
				if(webcast.webcastStatus.isInProgress || webcast.webcastStatus.isBroadcasting) {
					return;
				}

				this.updateWebexMeetingEvent(webcast, run);
			},

			WebcastVideoSourceStarted: msgData => {
				webcast.automatedWebcast = msgData.automatedWebcast;
				webcast.startingVideoSource = msgData.webcastVideoSource;
				webcast.deviceType = msgData.webcastDeviceType;
			},

			BroadcastStarted: () => {
				//incase of OEM or WebexLiveStream, BroadcastStarted does not guranteed that playback is available. So, Don't fetch playback.
				if((!webcast.currentUser.isEventAdminOrModerator && !webcast.vcStatus?.waitingForStream) ||
					webcast.isVodSource) {
					this.updateWebcastPlaybacks(webcast)
						.then(() => {
							webcast.webcastStatus.isBroadcasting = true;
							webcast.broadcastTime = moment().toString();
							setTimeout(() => this.zone.run(noop));
						})
						.catch(e => console.error('updateWebcastPlaybacks', e));
					return;
				}
				webcast.webcastStatus.isBroadcasting = true;
			},

			BroadcastStopped: () => {
				webcast.webcastStatus.isBroadcasting = false;
			},

			WebcastEnded: ({ runNumber, nextRun, endDate, status }) => {
				//there two types of WebcastEnded. one with status = 'Complete' and another with status = 'Restarting'.
				webcast.isSecondaryPlaying = false;
				webcast.isPassiveStreamReady = false;
				webcast.primaryPlaybacks = null;
				webcast.secondaryPlaybacks = null;

				if (status === WebcastStatus.Restarting) {
					return;
				}
				webcast.status = runNumber === webcast.productionRun.runNumber ? WebcastStatus.Completed : WebcastStatus.Scheduled;
				this.updateRunItem(webcast, runNumber, run => {
					run.status = WebcastRunStatus.Ended;
					run.endDate = this.DateParsers.parseUTCDate(endDate);
				});

				webcast.playbacks = null;
				webcast.isPresenterReady = false;
				webcast.webRtcListenerUrl = null;

				if(nextRun) {
					webcast.addRunItem(nextRun);
				}
				webcast.update(webcast);
			},
			ScheduledEventStopped: () => {
				webcast.status = WebcastStatus.Scheduled;
				webcast.currentRun.status = WebcastRunStatus.Scheduled;
			},

			ScheduledAutomatedWebcastCancelled: () => {
				webcast.automatedWebcast = false;
			},

			WebcastFailed: ({ runNumber }) => {
				webcast.webcastStatus.isFailed = true;
				this.updateRunItem(webcast, runNumber, run => run.status = WebcastRunStatus.Scheduled);
			},

			StartWebcastCancelled: ({ runNumber, status }) => {
				webcast.automatedWebcast = false;
				webcast.status = status;
				webcast.vcStatus.isConnecting = false;
				webcast.update();

				this.updateRunItem(webcast, runNumber, run => run.status = WebcastRunStatus.Scheduled);

				const currentRunNumber = webcast.currentRun && webcast.currentRun.runNumber;
				if(runNumber === currentRunNumber) {
					webcast.webcastStatus.isCancelled = true;
				}
			},

			StartVodWebcastFailed: this.onVodWebcastFailed(webcast),

			StreamVodWebcastFailed: this.onVodWebcastFailed(webcast),

			VideoSourceEnableLogged: () => {
				if (webcast.isAutomated || webcast.currentUser.isEventAdmin) {
					webcast.waitingForVideoSource = true;	// Show the waiting msg for now.  It will be reset upon WebcastStarted
				}
				webcast.startingVideoSource = false;
			},

			DmeDeviceStartAkamaiDistributionActionResultRecorded: e => {
				if (webcast.isUpdatingPlaybacks) {
					return;
				}

				if (isAkamaiPushedPlaybackReady(webcast.playbacks, e.deviceId, e.streamName)
					&& (webcast.currentUser.isEventAdmin || webcast.currentUser.isEventModerator)) {
					this.waitForVideoSource(webcast);
				} else {
					webcast.waitingForVideoSource = false;
				}
			},

			DmeDeviceStartAkamaiDistributionActionNotAdded: e => {
				if (webcast.isUpdatingPlaybacks) {
					return;
				}

				if (isAkamaiPushedPlaybackReady(webcast.playbacks, e.deviceId, e.streamName)
					&& (webcast.currentUser.isEventAdmin || webcast.currentUser.isEventModerator)) {
					this.waitForVideoSource(webcast);
				} else {
					webcast.waitingForVideoSource = false;
				}
			},

			DmeDeviceStartCdnPushActionResultRecorded: e => {
				if (webcast.isUpdatingPlaybacks) {
					return;
				}

				if (isVbrickCdnPushedPlaybackReady(webcast.playbacks, e.deviceId, e.streamName)
					&& (webcast.currentUser.isEventAdmin || webcast.currentUser.isEventModerator)) {
					this.waitForVideoSource(webcast);
				} else {
					webcast.waitingForVideoSource = false;
				}
			},

			DmeDeviceStartCdnPushActionNotAdded: e => {
				if (webcast.isUpdatingPlaybacks) {
					return;
				}

				if (isVbrickCdnPushedPlaybackReady(webcast.playbacks, e.deviceId, e.streamName)
					&& (webcast.currentUser.isEventAdmin || webcast.currentUser.isEventModerator)) {
					this.waitForVideoSource(webcast);
				} else {
					webcast.waitingForVideoSource = false;
				}
			},

			VcWebcastStatusUpdated: ({ vcStatus, isSuccessful, readyToPlay, numSegmentsPushed, webRtcListenerUrl }) => {
				if (webcast.isSecondaryPlaying) {
					this.updatePassiveStreamStatus(webcast, vcStatus, readyToPlay, numSegmentsPushed);
					return;
				}
				if (webcast.isWebexLiveStreamType || webcast.isRtmpStreamType || webcast.isWebrtcSinglePresenter || webcast.isProducerEvent) {
					this.updateWebexMeetingByVCStatus(vcStatus, webcast, numSegmentsPushed);
				}

				//temp fix - incase of webexlive/oem, keep waiting till ui gets numSegmentsPushed > 0.
				//even if response has vcStatus = 'recording' && readyToPlay = true, playback is still not available.
				//Not able to maintain all these statuses now. Backend needs to stream line all these statuses ASAP.
				if (webcast.isWebexLiveStreamType && vcStatus === VC_STATUS.Recording && readyToPlay && !numSegmentsPushed) {
					this.updateWebexMeetingByVCStatus(VC_STATUS.WaitingForStream, webcast);
				} else {
					webcast.vcStatus.update(vcStatus, isSuccessful, readyToPlay);
				}

				if (vcStatus === VC_STATUS.Starting && isSuccessful) {
					setTimeout(() => {
						webcast.update({ webRtcListenerUrl });
					}, (webcast.isProducerEvent ? 0.5 : isApple() ? 10 : 5) * SecondMs);
				}

				if (vcStatus === VC_STATUS.Starting && webcast.vcStatus.connectionFailed) {
					webcast.status = WebcastStatus.Scheduled;
					webcast.currentRun.status = WebcastRunStatus.Scheduled;
					webcast.webRtcListenerUrl = null;
				}
			},

			SecondaryVcWebcastStatusUpdated: ({ vcStatus, isSuccessful, readyToPlay, numSegmentsPushed }) => {
				if(webcast.isSecondaryPlaying) {
					this.updateWebexMeetingByVCStatus(vcStatus, webcast, numSegmentsPushed);
					return;
				}
				this.updatePassiveStreamStatus(webcast, vcStatus, readyToPlay, numSegmentsPushed);
			},

			WebcastStreamSwitched: $event => {
				//this is to track vc status change when switching streams.
				const wasPreviousStreamWaiting = webcast.vcStatus.waitingForStream;

				webcast.vcStatus.update(webcast.isPassiveStreamReady ? VC_STATUS.LoadingSwitchStream : VC_STATUS.WaitingForStream, false, false);
				webcast.update({ isSecondaryPlaying: $event.secondary, waitingForVideoSource: true });
				if (webcast.isPassiveStreamReady && (webcast.webcastStatus.isInProgress || webcast.webcastStatus.isBroadcasting)) {
					this.updateWebcastPlaybacks(webcast, true);
				}

				//change the passive stream ready depending copied vcStatus.
				webcast.update({ isPassiveStreamReady: !wasPreviousStreamWaiting });
			},

			LinkedVideoUpdated: e => {
				webcast.update({
					linkedVideoId: e.videoId,
					redirectVod: e.redirectVod
				});

				this.updateLinkedVideo(webcast);
			},
			ProducerBgImageSaved: ({ imageId }) => {
				webcast.update({
					producerBgImages: [
						...webcast.producerBgImages,
						{ id: imageId }
					]
				});
			},
			ProducerBgImageRemoved: ({ imageId }) => {
				webcast.update({
					producerBgImages: [
						...webcast.producerBgImages.filter(img => img.id !== imageId)
					]
				});
			},
			ImageInstancesAdded: img => {
				webcast.update({
					producerBgImages: [
						...webcast.producerBgImages
							.map(oldImg => oldImg.id !== img.id ? oldImg : img)
					]
				});
			}
		});

		return this.PushBus.composeUnsubscribeFn([
			accountDevicesSubscription,
			webcastSubscription,
			webcastAdminSubscription,
			webcast.broadcast.subscribePush(),
			webcast.recording.subscribePush(),
			webcast.presentation.registerPushHandlers()
		].filter(Boolean));
	}

	public connect(webcast: WebcastModel): Promise<Subscription> {
		webcast.currentUser.sessionId = null;
		webcast.layout.reset();
		webcast.presentation.ignoreSlideChangeEvents(false);

		if(webcast.currentUser.isEventAdmin) {
			if(!webcast.isAutomated && !webcast.presentationProfileDeviceActive) {
				return Promise.reject('presentationProfileDeviceInactive');
			}

			if(webcast.presentation.ready){
				webcast.presentation.prefetchAllSlidesInSeries();
			}
		}

		return new Promise<Subscription>((resolve, reject) => {
			const sub = this.WebcastConnectionService.connect(webcast)
				.subscribe({
					next: () => setTimeout(() => resolve(sub)),
					error: err => {
						console.error('Webcast connection error: ', err);
						reject(err);
					}
				});
		})
			.then(sub => {

				//very hot place. Think before touching this.
				const skipUpdate = webcast.webcastStatus.isStarting
					|| webcast.webcastStatus.isWaitingForStream
					|| webcast.webcastStatus.isVideoSourceStarting
					|| webcast.vcStatus?.waitingForStream;

				if (!skipUpdate) {
					this.updateWebcastPlaybacks(webcast)
						.catch(e => console.error('updateWebcastPlaybacks', e));
				}
				webcast.polls.load();

				return sub;
			});
	}

	private updateRunItem(webcast: WebcastModel, runNumber: number, fn: (RunItem) => void): void {
		const run = webcast.getRunItem(runNumber);
		if(run){
			fn(run);
		}
	}

	private updatePassiveStreamStatus(webcast: WebcastModel, vcStatus: VC_STATUS, readyToPlay: boolean, numSegmentsPushed?: number) {
		const isPassiveStreamReady = vcStatus === VC_STATUS.Recording && readyToPlay && numSegmentsPushed > 0;
		if (isPassiveStreamReady !== webcast.isPassiveStreamReady) {
			webcast.update({ isPassiveStreamReady });
		}
	}

	private updateWebexMeetingEvent(webcast: WebcastModel, runItem: RunItem): void {
		webcast.webcastStatus.isVideoSourceStarting = true;
		updateWaitingStatusForWebexMeeting(webcast);
		this.updateRunItem(
			webcast,
			runItem.runNumber,
			run => {
				run.status = WebcastRunStatus.InProgress;
				run.startDate = this.DateParsers.parseUTCDate(runItem.startDate);
			}
		);
	}

	private updateWebexMeetingByVCStatus(vcStatus: VC_STATUS, webcast: WebcastModel, numSegmentsPushed?: number): void {
		if (vcStatus === VC_STATUS.WaitingForStream) {
			webcast.waitingForVideoSource = true;
			webcast.vcStatus.update(vcStatus);
		} else if (vcStatus === VC_STATUS.Recording
			&& (webcast.webcastStatus.isInProgress || webcast.webcastStatus.isBroadcasting)
			&& webcast.vcStatus.waitingForStream && numSegmentsPushed > 0) {
			this.updateWebcastPlaybacks(webcast)
				.catch(e => console.error('updateWebcastPlaybacks', e))
				.finally(() => {
					webcast.startingVideoSource = false;
					webcast.waitingForVideoSource = false;
					webcast.update();
				});
		}
	}

	private onVodWebcastFailed(webcast: WebcastModel): (event: any) => void {
		return ({ runNumber, status }) => {
			webcast.webcastStatus.isFailed = true;
			webcast.status = status;
			webcast.vcStatus.update(VC_STATUS.Starting, false);
			webcast.update();
			this.updateRunItem(webcast, runNumber, run => run.status = status);
		};
	}
}
